import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Navigate, useLocation } from 'react-router-dom';
import { Box, CircularProgress, Typography, Alert } from '@mui/material';
import { RootState, AppDispatch } from '../../store';
import { getCurrentUser, logout } from '../../store/slices/authSlice';
import { isDevelopment } from '../../config/api';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRole?: 'USER' | 'ADMIN' | 'MODERATOR';
  fallbackPath?: string;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requiredRole,
  fallbackPath = '/login'
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const location = useLocation();
  const { isAuthenticated, user, isLoading, token, error, isInitialized } = useSelector(
    (state: RootState) => state.auth
  );
  const [authCheckComplete, setAuthCheckComplete] = useState(false);

  // Explicit bypass only — never auto-bypass in development (Phase 0 security)
  const authBypass =
    import.meta.env.VITE_AUTH_BYPASS === 'true' ||
    (typeof window !== 'undefined' &&
      new URLSearchParams(window.location.search).get('authBypass') === '1');

  // Fetch user profile if we have a token but no user data
  useEffect(() => {
    if (authBypass && isDevelopment) {
      setAuthCheckComplete(true);
      return;
    }
    let timeoutId: NodeJS.Timeout;

    if (token && !user && !isLoading && !authCheckComplete) {
      console.log('🔐 Fetching user profile with token...');

      dispatch(getCurrentUser())
        .unwrap()
        .then((user) => {
          console.log('✅ User profile fetched successfully:', user.email);
          setAuthCheckComplete(true);
        })
        .catch((error) => {
          console.error('❌ Failed to fetch user profile:', error);
          // If token is invalid, clear auth and redirect
          if (error.includes('token') || error.includes('invalid') || error.includes('malformed')) {
            console.warn('🚨 Invalid token detected, logging out...');
            dispatch(logout());
          }
          setAuthCheckComplete(true);
        });

      // Set a shorter timeout to prevent infinite loading
      timeoutId = setTimeout(() => {
        console.warn('⏰ Authentication check timed out (5s), logging out...');
        dispatch(logout());
        setAuthCheckComplete(true);
      }, 5000); // 5 second timeout (reduced from 10s)
    } else if (!token) {
      console.log('🔓 No access token found, marking auth check complete');
      setAuthCheckComplete(true);
    } else if (user) {
      console.log('👤 User already loaded, marking auth check complete');
      setAuthCheckComplete(true);
    }

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [dispatch, token, user, isLoading, authCheckComplete]);

  // If there's an auth error, clear it and redirect to login
  useEffect(() => {
    if (error && authCheckComplete) {
      console.warn('Authentication error:', error);
      dispatch(logout());
    }
  }, [error, authCheckComplete, dispatch]);

  if (authBypass && isDevelopment) {
    return (
      <>
        <Alert severity="warning" sx={{ m: 2 }}>
          Auth bypass active (VITE_AUTH_BYPASS / ?authBypass=1) — not for production
        </Alert>
        {children}
      </>
    );
  }

  // Wait for AuthBootstrap before deciding
  if (!isInitialized) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          gap: 2,
        }}
      >
        <CircularProgress size={40} />
        <Typography variant="body1" color="text.secondary">
          Loading session...
        </Typography>
      </Box>
    );
  }

  // Show loading spinner while checking authentication
  if (!authCheckComplete || (isLoading && token && !user)) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          gap: 2,
        }}
      >
        <CircularProgress size={40} />
        <Typography variant="body1" color="text.secondary">
          Verifying authentication...
        </Typography>
        <Typography variant="caption" color="text.disabled">
          This should only take a moment
        </Typography>
      </Box>
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated || !user) {
    return (
      <Navigate
        to={fallbackPath}
        state={{ from: location }}
        replace
      />
    );
  }

  // Check role-based access if required
  if (requiredRole && user.role !== requiredRole) {
    // For role-based access, you might want to show an access denied page
    // or redirect to a different route
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          gap: 2,
          p: 3,
        }}
      >
        <Typography variant="h5" color="error" gutterBottom>
          Access Denied
        </Typography>
        <Typography variant="body1" color="text.secondary" textAlign="center">
          You don't have the required permissions to access this page.
          <br />
          Required role: {requiredRole}
          <br />
          Your role: {user.role}
        </Typography>
      </Box>
    );
  }

  // User is authenticated and has required permissions
  return <>{children}</>;
};

export default ProtectedRoute;
